import * as React from 'react';

type SlotAttributes = Readonly<{ __isSlot: true; __slotName: string }>;

export type SlotComponent = React.FC<{ children?: React.ReactNode }> &
  SlotAttributes;

type SlotsObject<Names extends readonly string[]> = {
  [K in Names[number] as Capitalize<K>]: SlotComponent;
};

type ExtractedSlots<Names extends readonly string[]> = Partial<
  Record<Names[number], React.ReactNode>
>;

function capitalize<S extends string>(s: S): Capitalize<S> {
  return (s.charAt(0).toUpperCase() + s.slice(1)) as Capitalize<S>;
}

function createSlotComponent(
  rootName: string,
  name: string,
  cap: string,
): SlotComponent {
  const Comp: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <>{children}</>
  );

  Object.defineProperty(Comp, '__isSlot', { value: true, enumerable: false });
  Object.defineProperty(Comp, '__slotName', { value: name, enumerable: false });
  Object.defineProperty(Comp, 'displayName', {
    value: `${rootName}.${cap}`,
    enumerable: false,
  });

  return Comp as SlotComponent;
}

export function createSlots<const Names extends readonly string[]>(
  rootName: string,
  slotNames: Names,
) {
  const entries = slotNames.map((name) => {
    const cap = capitalize(name);
    const comp = createSlotComponent(rootName, name, cap);
    return [cap, comp] as const;
  });

  const slots = Object.fromEntries(entries) as SlotsObject<Names>;

  function useSlots(children: React.ReactNode): ExtractedSlots<Names> {
    const result: Partial<ExtractedSlots<Names>> = {};

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;

      const type = child.type as Partial<SlotAttributes>;
      if (type?.__isSlot && typeof type.__slotName === 'string') {
        const name = type.__slotName as Names[number];
        result[name] ??= (
          child.props as { children?: React.ReactNode }
        ).children;
      }
    });

    return result as ExtractedSlots<Names>;
  }

  return { slots, useSlots };
}
